import type { 
  ApiResponse, 
  CaptchaVO, 
  LoginDTO, 
  RegisterDTO, 
  UserVO, 
  AdminVO,
  ArticleVO, 
  PageResult, 
  PageQuery,
  LoginResult,
  TokenInfo,
  ProductVO,
  CartItem,
  CartEntity,
  OrderVO,
  CreateOrderDTO,
  BuyerVO,
  SupplierVO,
  OrderFeePaymentVO,
  FeePaymentSubmitDTO,
  ShipProofSubmitDTO,
  ReceiveProofSubmitDTO,
  BatchFeeSettleSubmitDTO,
  OrderFeeSettleBatchVO,
} from '@/types/api-entity';
import type { eCard, CardTemplateVO, EnterpriseInfoVO, RichTextItem, PersonnelDetailVO, PersonnelVO, PersonnelUserVO } from '@/types/eCard'
const BASE = import.meta.env.VITE_API_BASE_URL || 'http://172.16.163.44:8080';
// API 基础地址
let API_BASE = ''
if(BASE.includes('https')){
  API_BASE = BASE + '/api'
}else{
  API_BASE = BASE 
}


// ===================== Token 管理工具 =====================
const TOKEN_KEY = 'token';
const USER_TYPE_KEY = 'userType';
const USER_INFO_KEY = 'userInfo';
const ADMIN_INFO_KEY = 'adminInfo';
const TENANT_INFO_KEY = 'tenantInfo';

const tokenStorage = {
  // 保存登录信息
  setLoginInfo: (token: string, userType: 'user' | 'admin', userInfo: UserVO | AdminVO, tenantInfo?: any) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_TYPE_KEY, userType);
    if (userType === 'user') {
      localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
      localStorage.removeItem(ADMIN_INFO_KEY);
    } else {
      localStorage.setItem(ADMIN_INFO_KEY, JSON.stringify(userInfo));
      localStorage.removeItem(USER_INFO_KEY);
    }
    if (tenantInfo) {
      localStorage.setItem(TENANT_INFO_KEY, JSON.stringify(tenantInfo));
    } else {
      localStorage.removeItem(TENANT_INFO_KEY);
    }
  },
  
  // 保存租户信息
  setTenantInfo: (tenantInfo: any) => {
    localStorage.setItem(TENANT_INFO_KEY, JSON.stringify(tenantInfo));
  },
  
  // 获取租户信息
  getTenantInfo: (): any => {
    const data = localStorage.getItem(TENANT_INFO_KEY);
    return data ? JSON.parse(data) : null;
  },
  
  // 获取 Token
  getToken: (): string | null => localStorage.getItem(TOKEN_KEY),
  
  // 获取用户类型
  getUserType: (): 'user' | 'admin' | null => {
    const type = localStorage.getItem(USER_TYPE_KEY);
    return type as 'user' | 'admin' | null;
  },
  
  // 获取用户信息
  getUserInfo: (): UserVO | null => {
    const data = localStorage.getItem(USER_INFO_KEY);
    return data ? JSON.parse(data) : null;
  },
  
  // 获取管理员信息
  getAdminInfo: (): AdminVO | null => {
    const data = localStorage.getItem(ADMIN_INFO_KEY);
    return data ? JSON.parse(data) : null;
  },
  
  // 获取当前登录信息（通用）
  getCurrentUser: (): UserVO | AdminVO | null => {
    const userType = tokenStorage.getUserType();
    if (userType === 'user') return tokenStorage.getUserInfo();
    if (userType === 'admin') return tokenStorage.getAdminInfo();
    return null;
  },
  
  // 判断是否登录
  isLoggedIn: (): boolean => !!localStorage.getItem(TOKEN_KEY),
  
  // 判断是否管理员登录
  isAdminLoggedIn: (): boolean => {
    const token = localStorage.getItem(TOKEN_KEY);
    const userType = localStorage.getItem(USER_TYPE_KEY);
    return !!token && userType === 'admin';
  },
  
  // 判断是否用户登录
  isUserLoggedIn: (): boolean => {
    return tokenStorage.isLoggedIn() && tokenStorage.getUserType() === 'user';
  },
  
  // 清除登录信息（登出）
  clearLoginInfo: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_TYPE_KEY);
    localStorage.removeItem(USER_INFO_KEY);
    localStorage.removeItem(ADMIN_INFO_KEY);
    localStorage.removeItem(TENANT_INFO_KEY);
  },
};

// ===================== 请求封装（带 Token） =====================
async function request<T>(url: string, options?: RequestInit): Promise<ApiResponse<T>> {
  // 构建请求头
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  // 自动带上 Token
  const token = tokenStorage.getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE}${url}`, {
    headers,
    ...options,
  });
  
  // 401 未登录或 Token 过期，清除登录信息并跳转登录页
  if (response.status === 401) {
    tokenStorage.clearLoginInfo();
    // 判断当前是前台还是后台，跳转到对应登录页
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/admin')) {
      window.location.href = '/admin/login';
    } else {
      window.location.href = '/login';
    }
    throw new Error('登录已过期，请重新登录');
  }
  
  // 403 无权限
  if (response.status === 403) {
    throw new Error('无权访问');
  }
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
}

// ===================== 验证码服务 =====================
export const captchaApi = {
  getCaptcha: () => request<CaptchaVO>('/auth/captcha/image'),
};

// ===================== 用户服务（前台） =====================
export const userApi = {
  // 用户登录
  login: async (data: LoginDTO): Promise<ApiResponse<LoginResult>> => {
    const res = await request<LoginResult>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    // 登录成功，保存 Token 和用户信息
    if (res.code === 200 && res.data) {
      const { token, user, tenant } = res.data;
      if (token && user) {
        tokenStorage.setLoginInfo(token, 'user', user, tenant);
      }
    }
    
    return res;
  },
  
  // 用户注册
  register: (data: RegisterDTO) => request<UserVO>('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // 获取当前登录用户信息（需要登录）
  getCurrentUser: () => request<UserVO>('/me/info'),
  
  // 用户登出
  logout: async (): Promise<void> => {
    try {
      await request<void>('/auth/logout', { method: 'POST' });
    } finally {
      tokenStorage.clearLoginInfo();
    }
  },
};

// ===================== 管理员服务（后台） =====================
export const adminApi = {
  // 管理员登录
  login: async (data: LoginDTO): Promise<ApiResponse<LoginResult>> => {
    const res = await request<LoginResult>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // 登录成功，保存 Token 和管理员信息
    if (res.code === 200 && res.data) {
      const { token, user } = res.data;
      if (token && user) {
        tokenStorage.setLoginInfo(token, 'admin', user);
      }
    }
    
    return res;
  },
  
  // 获取当前登录管理员信息（需要登录）
  getCurrentAdmin: () => request<AdminVO>('/me/info'),
  
  // 管理员登出
  logout: async (): Promise<void> => {
    try {
      await request<void>('/auth/logout', { method: 'POST' });
    } finally {
      tokenStorage.clearLoginInfo();
    }
  },
};

// ===================== 文章服务 =====================
export const articleApi = {
  // ========== 前台公开接口（无需登录） ==========
  
  getArticleList: (params?: PageQuery & { category?: string; keyword?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.pageNum) queryParams.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.keyword) queryParams.append('keyword', params.keyword);
    
    return request<PageResult<ArticleVO>>(`/pub/article/list?${queryParams.toString()}`);
  },
  
  getArticleDetail: (id: number) => request<ArticleVO>(`/pub/article/${id}`),
  
  getCategories: () => request<string[]>('/pub/article/categories'),
  
  // ========== 后台管理接口（需要 admin 登录） ==========
  
  getAdminArticleList: (params?: PageQuery & { category?: string; keyword?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.pageNum) queryParams.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.keyword) queryParams.append('keyword', params.keyword);
    
    return request<PageResult<ArticleVO>>(`/admin/article/page?${queryParams.toString()}`);
  },
  
  getAdminArticleDetail: (id: number) => request<ArticleVO>(`/admin/article/${id}`),
  
  createArticle: (data: Partial<ArticleVO>) => request<ArticleVO>('/admin/article', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateArticle: (id: number, data: Partial<ArticleVO>) => request<ArticleVO>(`/admin/article/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  deleteArticle: (id: number) => request<void>(`/admin/article/${id}`, { method: 'DELETE' }),
  
  publishArticle: (id: number) => request<void>(`/admin/article/${id}/publish`, { method: 'PUT' }),
  
  offlineArticle: (id: number) => request<void>(`/admin/article/${id}/offline`, { method: 'PUT' }),
  
  topArticle: (id: number, isTop: number) => request<void>(`/admin/article/${id}/top/${isTop}`, { method: 'PUT' }),

  checkArticleStatus: (id: number,status: number) => request<void>(`/admin/article/${id}/check?status=${status}`, { method: 'PUT' }),
};

// ===================== 文件上传服务 =====================
export const uploadApi = {
  /**
   * 上传图片文件
   * @param file 图片文件
   * @returns 上传后的图片URL
   */
  uploadImage: async (file: File): Promise<string | null> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // 上传请求也需要带 Token
      const headers: Record<string, string> = {};
      const token = tokenStorage.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE}/admin/upload/image`, {
        method: 'POST',
        headers,
        body: formData,
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          tokenStorage.clearLoginInfo();
          window.location.href = '/admin/login';
          throw new Error('登录已过期');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      console.log('图片上传返回:', result);
      
      if (result.code === 200) {
        if (result.data?.url) {
          return result.data.url;
        } else if (result.data?.fileUrl) {
          return result.data.fileUrl;
        } else if (result.data) {
          return String(result.data);
        } else if (result.url) {
          return result.url;
        }
      }
      return null;
    } catch (error) {
      console.error('图片上传失败:', error);
      return null;
    }
  },
  uploadFile: async (file: File): Promise<string | null> => {
    try {
      console.log(file,'file')
      const formData = new FormData();
      formData.append('file', file);
      console.log(formData,'formData')
      const headers: Record<string, string> = {};
      const token = tokenStorage.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE}/admin/upload/file`, {
        method: 'POST',
        headers,
        body: formData,
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          tokenStorage.clearLoginInfo();
          window.location.href = '/admin/login';
          throw new Error('登录已过期');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      console.log('文件上传返回:', result);
      if (result.code === 200) {
        if (typeof result.data === 'string') {
          return result.data;
        } else if (result.data?.url) {
          return result.data.url;
        } else if (result.data?.fileUrl) {
          return result.data.fileUrl;
        } else if (result.url) {
          return result.url;
        }
      }
      return null;
    } catch (error) {
      console.error('文件上传失败:', error);
      return null;
    }
  },
};

// ===================== 商城商品服务 =====================
export const shopApi = {
  // 商品分页列表
  getProductList: (params?: PageQuery & { keyword?: string; category?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.pageNum) queryParams.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.keyword) queryParams.append('keyword', params.keyword);
    if (params?.category) queryParams.append('category', params.category);
    return request<PageResult<ProductVO>>(`/pub/product/list?${queryParams.toString()}`);
  },

  // 商品详情
  getProductDetail: (id: number) => request<ProductVO>(`/pub/product/${id}`),

  // 商品分类
  getProductCategories: () => request<string[]>('/pub/product/categories'),
};

// ===================== 购物车 API（对接后端）=====================
export const cartApi = {
  // 加入购物车
  addToCart: (productId: number, quantity: number) =>
    request<void>('/buyer/cart/add', {
      method: 'POST',
      body: JSON.stringify({ productId, quantity }),
    }),

  // 获取购物车列表
  getCartList: () => request<CartEntity[]>('/buyer/cart/list'),

  // 更新商品数量
  updateQuantity: (productId: number, quantity: number) =>
    request<void>(`/buyer/cart/${productId}`, {
      method: 'PUT',
      body: JSON.stringify({ quantity }),
    }),

  // 删除商品
  removeFromCart: (productId: number) =>
    request<void>(`/buyer/cart/${productId}`, { method: 'DELETE' }),

  // 清空购物车
  clearCart: () => request<void>('/buyer/cart/clear', { method: 'DELETE' }),
};

// ===================== 订单 API（采购商）=====================
export const orderApi = {
  // 创建订单（从购物车结算）
  createOrder: (data: CreateOrderDTO) =>
    request<OrderVO[]>('/buyer/order/create', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 查询我的订单列表
  getOrderList: () => request<OrderVO[]>('/buyer/order/list'),

  // 查询订单详情
  getOrderDetail: (id: number) => request<OrderVO>(`/buyer/order/${id}`),

  // 供应商接单
  acceptOrder: (id: number, data?: { contractAmount?: string; contractFile?: string }) =>
    request<void>(`/supplier/order/${id}/accept`, { method: 'PUT', body: data ? JSON.stringify(data) : undefined }),

  // 供应商拒单
  rejectOrder: (id: number) =>
    request<void>(`/supplier/order/${id}/reject`, { method: 'PUT' }),

  // 查询订单服务费凭证列表（供应商/采购商通用）
  getFeePayments: (id: number) =>
    request<OrderFeeSettleBatchVO[]>(`/supplier/order/${id}/fee-payments`),

  // 供应商提交服务费凭证
  submitFeePayment: (data: FeePaymentSubmitDTO) =>
    request<void>('/supplier/order/pay-fee', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 供应商发货（带发货凭证）
  shipOrder: (data: ShipProofSubmitDTO) =>
    request<void>('/supplier/order/ship', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // 采购商确认收货（带收货凭证）
  confirmReceive: (data: ReceiveProofSubmitDTO) =>
    request<void>('/buyer/order/confirm-receive', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // 供应商批量结算服务费
  batchSettleFeeSupplier: (data: BatchFeeSettleSubmitDTO) =>
    request<OrderFeeSettleBatchVO>('/supplier/order/fee/settle', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 采购商批量结算服务费
  batchSettleFeeBuyer: (data: BatchFeeSettleSubmitDTO) =>
    request<OrderFeeSettleBatchVO>('/buyer/order/fee/settle', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 查询供应商发起的结算列表
  getSupplierSettleList: (params?: PageQuery) => {
    const queryParams = new URLSearchParams();
    if (params?.pageNum) queryParams.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    return request<PageResult<OrderFeeSettleBatchVO>>(`/supplier/order/fee/settle/page?${queryParams.toString()}`);
  },

  // 查询采购商发起的结算列表
  getBuyerSettleList: (params?: PageQuery) => {
    const queryParams = new URLSearchParams();
    if (params?.pageNum) queryParams.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    return request<PageResult<OrderFeeSettleBatchVO>>(`/buyer/order/fee/settle/page?${queryParams.toString()}`);
  },

  // 查询结算详情（供应商）
  getSupplierSettleDetail: (paymentId: number) =>
    request<OrderFeeSettleBatchVO>(`/supplier/order/fee/settle/${paymentId}`),

  // 查询结算详情（采购商）
  getBuyerSettleDetail: (paymentId: number) =>
    request<OrderFeeSettleBatchVO>(`/buyer/order/fee/settle/${paymentId}`),
};

// ===================== 用户个人中心 API =====================
export const profileApi = {
  // === 采购商资料 ===
  getMyBuyer: () => request<BuyerVO>('/me/buyer'),
  saveMyBuyer: (data: Partial<BuyerVO>) =>
    request<BuyerVO>('/me/buyer', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // === 供应商资料 ===
  getMySupplier: () => request<SupplierVO>('/me/supplier'),
  saveMySupplier: (data: Partial<SupplierVO>) =>
    request<SupplierVO>('/me/supplier', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // === 商品管理（供应商） ===
  getMyProducts: (params?: PageQuery & { status?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.pageNum) queryParams.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.status !== undefined) queryParams.append('status', params.status.toString());
    return request<PageResult<ProductVO>>(`/supplier/product/list?${queryParams.toString()}`);
  },
  createMyProduct: (data: Partial<ProductVO>) =>
    request<ProductVO>('/supplier/product', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateMyProduct: (id: number, data: Partial<ProductVO>) =>
    request<ProductVO>(`/supplier/product/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  toggleMyProductStatus: (id: number, status: number) =>
    request<void>(`/supplier/product/${id}/status/${status}`, { method: 'PUT' }),

  // === 订单查看 ===
  getMyOrders: () => request<OrderVO[]>('/buyer/order/list'),
  getMySupplierOrders: () => request<OrderVO[]>('/supplier/order/list'),
};

// ===================== 购物车本地存储工具（未登录回退）=====================
const CART_KEY = 'shop_cart';

export const cartStorage = {
  // 获取购物车列表
  getCart: (): CartItem[] => {
    const data = localStorage.getItem(CART_KEY);
    return data ? JSON.parse(data) : [];
  },

  // 保存购物车
  saveCart: (cart: CartItem[]) => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  },

  // 添加商品到购物车（本地）
  addToCart: (product: ProductVO, quantity: number) => {
    const cart = cartStorage.getCart();
    const existing = cart.find((item) => item.productId === product.id);
    if (existing) {
      existing.quantity += quantity;
    } else {
      cart.push({
        productId: product.id,
        productName: product.productName,
        mainImage: product.mainImage,
        price: product.price,
        quantity,
        unit: product.unit,
        selected: true,
      });
    }
    cartStorage.saveCart(cart);
  },

  // 更新商品数量（本地）
  updateQuantity: (productId: number, quantity: number) => {
    const cart = cartStorage.getCart();
    const item = cart.find((item) => item.productId === productId);
    if (item) {
      item.quantity = Math.max(1, quantity);
      cartStorage.saveCart(cart);
    }
  },

  // 更新选中状态
  updateSelected: (productId: number, selected: boolean) => {
    const cart = cartStorage.getCart();
    const item = cart.find((item) => item.productId === productId);
    if (item) {
      item.selected = selected;
      cartStorage.saveCart(cart);
    }
  },

  // 全选/取消全选
  selectAll: (selected: boolean) => {
    const cart = cartStorage.getCart();
    cart.forEach((item) => (item.selected = selected));
    cartStorage.saveCart(cart);
  },

  // 删除商品（本地）
  removeFromCart: (productId: number) => {
    const cart = cartStorage.getCart().filter((item) => item.productId !== productId);
    cartStorage.saveCart(cart);
  },

  // 清空购物车（本地）
  clearCart: () => {
    localStorage.removeItem(CART_KEY);
  },

  // 获取购物车商品总数
  getCartCount: (): number => {
    return cartStorage.getCart().reduce((sum, item) => sum + item.quantity, 0);
  },

  // 获取选中商品总价
  getSelectedTotal: (): number => {
    return cartStorage
      .getCart()
      .filter((item) => item.selected)
      .reduce((sum, item) => sum + item.price * item.quantity, 0);
  },

  // 获取选中的购物车项
  getSelectedItems: (): CartItem[] => {
    return cartStorage.getCart().filter((item) => item.selected);
  },
};

// ===================== 导出存储工具 =====================
export { tokenStorage };

// ===================== 兼容旧版 storage 导出（废弃，建议迁移到 tokenStorage） =====================
export const storage = {
  setUserInfo: (userInfo: UserVO) => {
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
  },
  getUserInfo: (): UserVO | null => tokenStorage.getUserInfo(),
  getCurrentUser: (): UserVO | null => tokenStorage.getCurrentUser(),
  clearUserInfo: () => tokenStorage.clearLoginInfo(),
  isLoggedIn: () => tokenStorage.isLoggedIn(),
};

// ===================== 电子名片管理 API =====================
export const businessCardApi = {
  // 分页查询名片列表
  getPageList: (params?: { pageNum?: number; pageSize?: number; status?: number; keyword?: string }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.status !== undefined && params.status !== null) query.append('status', params.status.toString());
    if (params?.keyword) query.append('keyword', params.keyword);
    return request<PageResult<any>>(`/admin/business-card/page?${query.toString()}`);
  },
  // 根据ID查询名片详情
  getById: (id: number) => request<any>(`/admin/business-card/${id}`),
  // 创建名片
  create: (data: any) => request<any>('/admin/business-card', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  // 更新名片
  update: (id: number, data: any) => request<any>(`/admin/business-card/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  // 删除名片
  delete: (id: number) => request<void>(`/admin/business-card/${id}`, { method: 'DELETE' }),
  // 批量删除名片
  deleteBatch: (ids: number[]) => request<void>(`/admin/business-card?ids=${ids.join(',')}`, { method: 'DELETE' }),
};

// ===================== 电子名片管理 API (Legacy: 租户人员) =====================
export const eCardApi = {
  // 分页查询租户人员列表（返回用户列表）
  getPageList: (params?: { pageNum?: number; pageSize?: number; name?: string; phone?: string; keyword?: string }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.name) query.append('name', params.name);
    if (params?.phone) query.append('phone', params.phone);
    if (params?.keyword) query.append('keyword', params.keyword);
    return request<PageResult<PersonnelUserVO>>(`/me/tenant-admin/personnel/page?${query.toString()}`);
  },
  // 根据ID查询租户人员详情
  getById: (id: number) => request<PersonnelDetailVO>(`/me/tenant-admin/personnel/${id}`),
  // 创建租户人员
  create: (data: Partial<PersonnelVO>) => request<PersonnelDetailVO>('/me/tenant-admin/personnel', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  // 更新租户人员
  update: (data: Partial<PersonnelVO>) => request<PersonnelDetailVO>(`/me/tenant-admin/personnel`, {
    method: 'PUT',
    body: JSON.stringify({ ...data }),
  }),
  // 删除租户人员
  delete: (id: number) => request<void>(`/me/tenant-admin/personnel/${id}`, { method: 'DELETE' }),
  // 批量导入租户人员
  import: (data: FormData) => fetch(`${API_BASE}/me/tenant-admin/personnel/import`, {
    method: 'POST',
    headers: {
      Authorization: tokenStorage.getToken() ? `Bearer ${tokenStorage.getToken()}` : '',
    },
    body: data,
  }).then(async (res) => {
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    return res.json();
  }),
};

// ===================== 租户端电子名片管理 API =====================
export const tenantAdminBusinessCardApi = {
  // 分页查询电子名片列表
  getPageList: (params?: { pageNum?: number; pageSize?: number; status?: number; keyword?: string }) => {
    const query = new URLSearchParams();
    if (params?.pageNum) query.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) query.append('pageSize', params.pageSize.toString());
    if (params?.status !== undefined && params.status !== null) query.append('status', params.status.toString());
    if (params?.keyword) query.append('keyword', params.keyword);
    return request<PageResult<any>>(`/me/tenant-admin/business-cards/page?${query.toString()}`);
  },
  // 根据ID查询名片详情
  getById: (id: number) => request<any>(`/me/tenant-admin/business-cards/${id}`),
  // 为指定员工创建额外电子名片
  create: (userId: number, data: any) => request<any>(`/me/tenant-admin/business-cards/${userId}`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  // 更新名片
  update: (id: number, data: any) => request<any>(`/me/tenant-admin/business-cards/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  // 删除名片
  delete: (id: number) => request<void>(`/me/tenant-admin/business-cards/${id}`, { method: 'DELETE' }),
  // 批量删除名片
  deleteBatch: (ids: number[]) => request<void>(`/me/tenant-admin/business-cards?ids=${ids.join(',')}`, { method: 'DELETE' }),
};

// ===================== 企业名片模板 API =====================
export const cardTemplateApi = {
  getTemplate: () => request<CardTemplateVO>('/me/tenant-admin/card-template'),
  updateTemplate: (data: Partial<CardTemplateVO>) => request<CardTemplateVO>('/me/tenant-admin/card-template', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  saveTemplate: (data: Partial<CardTemplateVO>) => request<CardTemplateVO>('/me/tenant-admin/card-template', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// ===================== 企业信息 API =====================
export const enterpriseInfoApi = {
  getInfo: () => request<EnterpriseInfoVO>('/me/tenant-admin/enterprise-info'),
  updateInfo: (data: Partial<EnterpriseInfoVO>) => request<EnterpriseInfoVO>('/me/tenant-admin/enterprise-info', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// ===================== 企业微官网 API =====================
export const enterpriseSiteApi = {
  // 获取企业微官网
  get: () => request<any>('/me/tenant-admin/enterprise-site'),
  // 更新企业微官网（不存在则创建，存在则更新）
  update: (data: any) => request<any>('/me/tenant-admin/enterprise-site', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};